"use client";
 
import Link from "next/link";
import { Smartphone, MessagesSquare, Briefcase, Video, ArrowRight } from "lucide-react";
import { siteConfig, footerLinks } from "@/lib/data";
 
export default function Footer() {
  const year = new Date().getFullYear();
 
  return (
    <footer className="border-t border-hairline bg-white">
      <div className="container-px mx-auto max-w-7xl py-16">
        <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1.2fr]">
          <div>
            <Link href="/" className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl text-white">
                <img src="/logo/logo1.png" alt="Mobilink Logo" className="h-9 w-9" />
              </span>
              <span className="text-lg font-bold tracking-tight text-ink">Mobilink</span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted">
              Secure, permission-based mobile screen sharing for support teams who need to see the issue, not just hear about it.
            </p>
            <div className="mt-6 flex gap-3">
              {[MessagesSquare, Briefcase, Video].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="Social link"
                  className="focus-ring flex h-9 w-9 items-center justify-center rounded-full border border-hairline text-muted transition-colors hover:border-primary hover:text-primary"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
 
          <FooterColumn title="Product" links={footerLinks.product} />
          <FooterColumn title="Company" links={footerLinks.company} />
          <FooterColumn title="Resources" links={footerLinks.resources} />
 
          <div>
            <h3 className="text-sm font-semibold text-ink">Stay in the loop</h3>
            <p className="mt-3 text-sm text-muted">
              Product updates and support best practices, roughly once a month.
            </p>
            <form className="mt-4 flex items-center gap-2" onSubmit={(e) => e.preventDefault()}>
              <label htmlFor="footer-email" className="sr-only">
                Email address
              </label>
              <input
                id="footer-email"
                type="email"
                required
                placeholder="you@company.com"
                className="focus-ring w-full rounded-full border border-hairline bg-white px-4 py-2.5 text-sm text-ink placeholder:text-muted"
              />
              <button
                type="submit"
                aria-label="Subscribe"
                className="focus-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white transition-colors hover:bg-primary-dark"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
 
        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-hairline pt-8 text-sm text-muted sm:flex-row">
          <p>© {year} {siteConfig.sponsorName}. All rights reserved.</p>
          <div className="flex gap-6">
            <Link href="/privacy-policy" className="hover:text-primary">Privacy Policy</Link>
            <Link href="/terms" className="hover:text-primary">Terms & Conditions</Link>
            <Link href="/refund-policy" className="hover:text-primary">Refund & Cancellation Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
 
function FooterColumn({ title, links }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      <ul className="mt-4 space-y-3">
        {links.map((link) => (
          <li key={link.href}>
            <Link href={link.href} className="text-sm text-muted transition-colors hover:text-primary">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}